import { useCallback, useMemo, useState, useRef } from 'react'
import { FilePen, Upload, FileText, X, AlertCircle, Shield, Lock, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react'
import { PDFDocument } from 'pdf-lib'
import toast from 'react-hot-toast'
import { useAuth } from '@/shared/contexts/AuthContext'
import { computeFileSHA256 } from '@/shared/lib/crypto'
import { stampOntoPdfAtCoords, MIN_QR_PT } from '@/shared/lib/pdf-utils'
import { generateShortId } from '@/shared/lib/shortId'
import { SignatureContentEditor } from './SignatureContentEditor'
import { PdfPreviewWithPlacement, type ViewportRect } from './PdfPreviewWithPlacement'
import { ElectronicSignatureResult } from './ElectronicSignatureResult'
import { createElectronicSignatureRecord } from '../services/electronicSigningApi'
import { signDocument } from '@/features/signing/services/signingApi'
import type { ElectronicSignatureMode, ElectronicSignatureRecord, BiometricSample } from '../types'

const MAX_PDF_SIZE = 10 * 1024 * 1024

function uuidV4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function ElectronicSigningPage() {
  const { user } = useAuth()

  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const [mode, setMode] = useState<ElectronicSignatureMode>('text_and_qr')
  const [signerName, setSignerName] = useState(user?.user_metadata?.full_name || '')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [drawnImageDataUrl, setDrawnImageDataUrl] = useState<string | null>(null)
  const [biometricData, setBiometricData] = useState<BiometricSample[]>([])

  const [signatureRect, setSignatureRect] = useState<ViewportRect | null>(null)
  const [qrRect, setQrRect] = useState<ViewportRect | null>(null)
  const [activeSlot, setActiveSlot] = useState<'signature' | 'qr'>('signature')

  const [certificate, setCertificate] = useState<File | null>(null)
  const [certPassword, setCertPassword] = useState('')
  const [showCertPassword, setShowCertPassword] = useState(false)
  const [showCertSection, setShowCertSection] = useState(false)
  const certInputRef = useRef<HTMLInputElement>(null)

  const [isSigning, setIsSigning] = useState(false)
  const [result, setResult] = useState<{
    record: ElectronicSignatureRecord
    signedPdfBytes: Uint8Array
    isDigital: boolean
  } | null>(null)

  const signatureId = useMemo(() => uuidV4(), [pdfFile, result])
  const shortId = useMemo(() => generateShortId(8), [pdfFile, result])
  const isDigitalMode = !!certificate && !!certPassword

  const handlePdfChange = (file: File | null) => {
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast.error('Solo se aceptan archivos PDF. Convertí .doc/.docx a PDF primero (Word > Guardar como PDF).')
      return
    }
    if (file.size > MAX_PDF_SIZE) {
      toast.error('El PDF supera los 10 MB.')
      return
    }
    setPdfFile(file)
    setSignatureRect(null)
    setQrRect(null)
    setActiveSlot('signature')
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handlePdfChange(f)
  }, [])

  const needsSignature = mode === 'text' || mode === 'text_and_qr' || mode === 'drawn' || mode === 'drawn_and_qr'
  const needsQr = mode === 'qr' || mode === 'text_and_qr' || mode === 'drawn_and_qr'
  const isDrawnMode = mode === 'drawn' || mode === 'drawn_and_qr'
  const isTextMode = mode === 'text' || mode === 'text_and_qr'
  const allRectsPlaced = (!needsSignature || !!signatureRect) && (!needsQr || !!qrRect)

  // Validacion sincrona del tamano del QR usando los rendered px del rect
  // (no necesita cargar el PDF). Asume que el render scale del PDF es ~1:1
  // con puntos para PDFs A4/letter; pdf-lib usa la misma escala en stamp.
  const qrSizeWarning = (() => {
    if (!needsQr || !qrRect) return null
    const minPxNeeded = MIN_QR_PT * (qrRect.pageRenderedWidth / Math.max(1, qrRect.pageRenderedWidth)) // 1:1 fallback
    // Ratio aproximado px → pt: usamos el pageRenderedWidth contra una pagina A4 (~595pt)
    const approxScale = qrRect.pageRenderedWidth > 0 ? qrRect.pageRenderedWidth / 595 : 1
    const availablePx = Math.min(qrRect.width, qrRect.height - 20)
    const availablePt = availablePx / approxScale
    if (availablePt < MIN_QR_PT) {
      const minRectPx = Math.ceil(MIN_QR_PT * approxScale + 24)
      return `El rectángulo del QR es chico para escanearlo bien. Hacelo de al menos ${minRectPx}×${minRectPx} px. (Actual: ${Math.round(qrRect.width)}×${Math.round(qrRect.height)})`
    }
    void minPxNeeded
    return null
  })()

  // Convierte un ViewportRect a coordenadas PDF (origin bottom-left).
  const toPdfPlacement = async (r: ViewportRect) => {
    const probePdfBytes = new Uint8Array(await pdfFile!.arrayBuffer())
    const probeDoc = await PDFDocument.load(probePdfBytes)
    const probePage = probeDoc.getPages()[r.pageNumber - 1]
    const { width: pdfW, height: pdfH } = probePage.getSize()
    const sx = pdfW / r.pageRenderedWidth
    const sy = pdfH / r.pageRenderedHeight
    return {
      pageNumber: r.pageNumber,
      x: r.x * sx,
      y: pdfH - (r.y + r.height) * sy,
      width: r.width * sx,
      height: r.height * sy,
      pdfPageWidth: pdfW,
      pdfPageHeight: pdfH,
    }
  }

  const handleSign = async () => {
    if (!user) { toast.error('Necesitás iniciar sesión'); return }
    if (!pdfFile) return
    if (needsSignature && !signatureRect) {
      toast.error('Falta posicionar la firma sobre el PDF'); return
    }
    if (needsQr && !qrRect) {
      toast.error('Falta posicionar el QR sobre el PDF'); return
    }
    if (isTextMode && !signerName.trim()) {
      toast.error('Ingresá el nombre que aparecerá firmado'); return
    }
    if (isDrawnMode && !drawnImageDataUrl) {
      toast.error('Dibujá tu firma en el canvas'); return
    }
    if (certificate && !certPassword) {
      toast.error('Ingresá la contraseña del certificado'); return
    }
    // Validar tamaño mínimo del QR si aplica
    if (needsQr && qrRect) {
      const p = await toPdfPlacement(qrRect)
      const availableQrSize = Math.min(p.width, p.height - 20)
      if (availableQrSize < MIN_QR_PT) {
        toast.error(`El rectángulo del QR es muy chico para que sea escaneable (mínimo ${MIN_QR_PT} pt). Redibujalo más grande.`)
        return
      }
    }

    setIsSigning(true)
    try {
      const originalHash = await computeFileSHA256(pdfFile)
      let workingBytes: Uint8Array = new Uint8Array(await pdfFile.arrayBuffer())
      let pdfPageWidth = 0, pdfPageHeight = 0
      let firstPlacement: { pageNumber: number; x: number; y: number; width: number; height: number } | null = null

      // Stamp y QR usan la URL CORTA (signwiz.app/v/<shortId>) para que entre
      // visualmente en el rect. La URL completa (signwiz.app/se/<uuid> o
      // /s/<uuid>) tambien funciona y se muestra en el resultado.
      const meta = {
        signerName: signerName.trim() || undefined,
        timestamp: new Date().toISOString(),
        verificationUrl: `${window.location.origin}/v/${shortId}`,
      }

      if (needsSignature && signatureRect) {
        const p = await toPdfPlacement(signatureRect)
        const sigContent = isDrawnMode
          ? { type: 'drawn' as const, drawnImageDataUrl: drawnImageDataUrl || undefined }
          : { type: 'text' as const, text: signerName.trim() }
        const stamped = await stampOntoPdfAtCoords(
          workingBytes,
          { pageNumber: p.pageNumber, x: p.x, y: p.y, width: p.width, height: p.height },
          sigContent,
          meta
        )
        workingBytes = stamped.stampedBytes
        pdfPageWidth = p.pdfPageWidth
        pdfPageHeight = p.pdfPageHeight
        firstPlacement = { pageNumber: p.pageNumber, x: p.x, y: p.y, width: p.width, height: p.height }
      }

      if (needsQr && qrRect && qrDataUrl) {
        const p = await toPdfPlacement(qrRect)
        const stamped = await stampOntoPdfAtCoords(
          workingBytes,
          { pageNumber: p.pageNumber, x: p.x, y: p.y, width: p.width, height: p.height },
          { type: 'qr', qrDataUrl },
          meta
        )
        workingBytes = stamped.stampedBytes
        pdfPageWidth = pdfPageWidth || p.pdfPageWidth
        pdfPageHeight = pdfPageHeight || p.pdfPageHeight
        if (!firstPlacement) firstPlacement = { pageNumber: p.pageNumber, x: p.x, y: p.y, width: p.width, height: p.height }
      }

      // Hash final del PDF firmado (con todos los stamps aplicados)
      const finalHashBuffer = await crypto.subtle.digest(
        'SHA-256',
        workingBytes.buffer.slice(workingBytes.byteOffset, workingBytes.byteOffset + workingBytes.byteLength) as ArrayBuffer
      )
      const finalHash = Array.from(new Uint8Array(finalHashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('')

      const stamped = {
        stampedBytes: workingBytes,
        signedHash: finalHash,
        pageWidth: pdfPageWidth,
        pageHeight: pdfPageHeight,
      }
      const placement = firstPlacement!

      const baseName = pdfFile.name.replace(/\.pdf$/i, '')
      const signedFilename = `${baseName}_firmado.pdf`

      // Tanto firma digital (con .p12 real) como electronica simple (cert
      // efimero PAdES Basic) pasan ahora por el backend para tener
      // criptografia real y compatibilidad con Adobe Reader.
      const stampedBlob = new Blob([stamped.stampedBytes.buffer.slice(stamped.stampedBytes.byteOffset, stamped.stampedBytes.byteOffset + stamped.stampedBytes.byteLength) as ArrayBuffer], { type: 'application/pdf' })

      const digitalResult = await signDocument(
        stampedBlob,
        isDigitalMode && certificate ? certificate : null,
        isDigitalMode ? certPassword : '',
        {
          signatureId,
          shortId,
          signerName: signerName.trim() || undefined,
          pdfFilename: pdfFile.name,
          useEphemeralCert: !isDigitalMode,
          ephemeralSignerEmail: !isDigitalMode ? user.email : undefined,
          biometricData: isDrawnMode && biometricData.length > 0 ? biometricData : undefined,
          signatureImageData: isDrawnMode && drawnImageDataUrl ? drawnImageDataUrl : undefined,
        }
      )
      const digitalBytes = Uint8Array.from(atob(digitalResult.signed_pdf_base64), c => c.charCodeAt(0))
      const certSubject = digitalResult.signer.certificate_subject || ''
      const isEphemeral = !isDigitalMode || certSubject.includes('Firma Efimera')
      const fakeRecord: ElectronicSignatureRecord = {
        id: digitalResult.signature_id,
        signature_id: digitalResult.signature_id,
        short_id: shortId,
        signer_user_id: user.id,
        signer_email: user.email!,
        signer_name: digitalResult.signer.name,
        signature_type: mode,
        signature_content_text: signerName.trim() || null,
        original_filename: digitalResult.original_filename,
        signed_filename: digitalResult.signed_filename,
        original_hash: originalHash,
        signed_hash: digitalResult.file_hash,
        hash_algorithm: 'SHA-256',
        page_number: placement.pageNumber,
        position_x: placement.x,
        position_y: placement.y,
        position_width: placement.width,
        position_height: placement.height,
        page_width: stamped.pageWidth,
        page_height: stamped.pageHeight,
        signed_at: digitalResult.signature_timestamp,
        user_agent: navigator.userAgent,
        legal_framework: isEphemeral ? 'AR-Ley25506-Art5' : 'AR-Ley25506-Art9',
        legal_implication: isEphemeral
          ? 'Firma electrónica simple con cert efímero PAdES Basic (art. 5 Ley 25.506). Criptográficamente verificable. El PDF se abre en Adobe Reader con seal de firma (warning amarillo "issuer no confiable" porque es self-signed). Tiene menor valor probatorio que la firma digital con CA.'
          : `Firma digital con certificado PKI (art. 9 Ley 25.506). Certificado: ${certSubject}. Tiene plena validez y presunción de autoría e integridad.`,
      }
      setResult({ record: fakeRecord, signedPdfBytes: digitalBytes, isDigital: !isEphemeral })
      toast.success(isEphemeral ? 'Documento firmado (firma electrónica PAdES)' : 'Documento firmado digitalmente con certificado')
      // Bloque legacy: el insert directo a sgw_electronic_signatures ya no
      // se usa, todo va via /api/sign-pdf que escribe a sgw_signatures.
      void createElectronicSignatureRecord
      void signedFilename
      scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('[ElectronicSigningPage] handleSign error:', err)
      const msg = err instanceof Error ? err.message : 'Error al firmar'
      toast.error(msg, { duration: 6000 })
    } finally {
      setIsSigning(false)
    }
  }

  const handleSignAnother = () => {
    setPdfFile(null)
    setSignatureRect(null)
    setQrRect(null)
    setActiveSlot('signature')
    setResult(null)
    setCertificate(null)
    setCertPassword('')
    setShowCertSection(false)
  }

  // Cuando cambia el modo, mantengo solo los rects relevantes y dirijo el activeSlot
  const handleModeChange = (next: ElectronicSignatureMode) => {
    setMode(next)
    if (next === 'text') {
      setQrRect(null)
      setActiveSlot('signature')
    } else if (next === 'qr') {
      setSignatureRect(null)
      setActiveSlot('qr')
    } else {
      // text_and_qr: empezamos por signature si no hay; sino vamos al que falte
      if (!signatureRect) setActiveSlot('signature')
      else if (!qrRect) setActiveSlot('qr')
    }
  }

  const handleCertificateChange = (file: File | null) => {
    if (!file) {
      setCertificate(null)
      return
    }
    const ext = file.name.toLowerCase()
    if (!ext.endsWith('.p12') && !ext.endsWith('.pfx')) {
      toast.error('Selecciona un archivo .p12 o .pfx')
      return
    }
    if (file.size > 100 * 1024) {
      toast.error('El certificado no puede superar 100 KB')
      return
    }
    setCertificate(file)
  }

  if (result) {
    return (
      <div className="max-w-3xl mx-auto py-8">
        <ElectronicSignatureResult
          record={result.record}
          signedPdfBytes={result.signedPdfBytes}
          isDigital={result.isDigital}
          onSignAnother={handleSignAnother}
        />
      </div>
    )
  }

  const canSign = !!pdfFile && allRectsPlaced && !isSigning && !!user && (!certificate || certPassword.length > 0) && !qrSizeWarning

  return (
    <div className="max-w-3xl mx-auto py-8 space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-600 dark:bg-primary-500 text-white mb-3">
          <FilePen size={32} />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-2">
          <span className="text-legal-900 dark:text-white">Firmar </span>
          <span className="text-primary-600 dark:text-primary-400">Documento</span>
        </h1>
        <p className="text-gray-600 dark:text-gray-300 max-w-xl mx-auto text-sm">
          Posicion&aacute; texto cursivo y QR sobre el PDF. Opcional: agreg&aacute; tu certificado .p12/.pfx
          para firma <strong>digital</strong> (art. 9 Ley 25.506). Sin certificado, queda como firma
          <strong> electr&oacute;nica</strong> (art. 5).
        </p>
      </div>

      <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md border border-legal-100 dark:border-legal-700 p-4 sm:p-6">
        <h2 className="text-lg font-bold text-legal-900 dark:text-white mb-1">1. Documento PDF</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Solo PDF. Máximo 10 MB.</p>

        {pdfFile ? (
          <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
            <FileText className="text-blue-600 dark:text-blue-400" size={18} />
            <div className="flex-1 min-w-0">
              <p className="text-blue-800 dark:text-blue-300 text-sm font-medium truncate">{pdfFile.name}</p>
              <p className="text-blue-600 dark:text-blue-400 text-xs">{(pdfFile.size / 1024).toFixed(0)} KB</p>
            </div>
            <button onClick={() => { setPdfFile(null); setSignatureRect(null); setQrRect(null) }} className="text-gray-400 hover:text-red-500">
              <X size={18} />
            </button>
          </div>
        ) : (
          <label
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
            onDragLeave={() => setIsDragOver(false)}
            className={`block cursor-pointer border-2 border-dashed rounded-lg px-4 sm:px-6 py-8 sm:py-10 text-center transition-colors ${
              isDragOver
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-legal-300 dark:border-legal-600 hover:border-primary-400'
            }`}
          >
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handlePdfChange(e.target.files?.[0] || null)}
            />
            <Upload className="mx-auto text-legal-400 mb-3" size={36} />
            <p className="text-legal-600 dark:text-legal-400 font-medium">
              Arrastra un PDF o hace clic para seleccionar
            </p>
          </label>
        )}
      </div>

      {pdfFile && (
        <SignatureContentEditor
          mode={mode}
          onModeChange={handleModeChange}
          signerName={signerName}
          onSignerNameChange={setSignerName}
          shortId={shortId}
          onQrChange={setQrDataUrl}
          onDrawnChange={({ imageDataUrl, biometricData }) => {
            setDrawnImageDataUrl(imageDataUrl)
            setBiometricData(biometricData)
          }}
        />
      )}

      {pdfFile && (
        <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md border border-legal-100 dark:border-legal-700 p-4 sm:p-6">
          <button
            type="button"
            onClick={() => setShowCertSection(s => !s)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDigitalMode
                  ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                  : 'bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300'
              }`}>
                <Shield size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-legal-900 dark:text-white">
                  3. Certificado digital <span className="text-sm font-normal text-gray-500">(opcional)</span>
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isDigitalMode
                    ? `Firma digital activa: ${certificate?.name}`
                    : 'Sin certificado: firma electrónica simple. Con certificado: firma digital con PKI.'}
                </p>
              </div>
            </div>
            {showCertSection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {showCertSection && (
            <div className="mt-5 space-y-4 pt-5 border-t border-legal-100 dark:border-legal-700">
              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                  Certificado (.p12 / .pfx)
                </label>
                {certificate ? (
                  <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3">
                    <Lock className="text-green-600 dark:text-green-400 flex-shrink-0" size={18} />
                    <span className="text-green-800 dark:text-green-300 text-sm font-medium flex-1 truncate">
                      {certificate.name}
                    </span>
                    <button
                      onClick={() => {
                        setCertificate(null)
                        setCertPassword('')
                        if (certInputRef.current) certInputRef.current.value = ''
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Quitar certificado"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => certInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-legal-300 dark:border-legal-600 rounded-lg px-4 py-3 text-legal-600 dark:text-legal-400 hover:border-primary-400 hover:text-primary-600 transition-colors"
                  >
                    <Upload size={18} />
                    Seleccionar certificado
                  </button>
                )}
                <input
                  ref={certInputRef}
                  type="file"
                  accept=".p12,.pfx"
                  onChange={(e) => handleCertificateChange(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </div>

              {certificate && (
                <div>
                  <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                    Contraseña del certificado
                  </label>
                  <div className="relative">
                    <input
                      type={showCertPassword ? 'text' : 'password'}
                      value={certPassword}
                      onChange={(e) => setCertPassword(e.target.value)}
                      placeholder="Contraseña"
                      className="w-full px-4 pr-12 py-2.5 rounded-lg border border-legal-200 dark:border-legal-600 bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCertPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showCertPassword ? 'Ocultar' : 'Mostrar'}
                      tabIndex={-1}
                    >
                      {showCertPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tu certificado se procesa en el servidor solo para firmar y no se almacena.
              </p>
            </div>
          )}
        </div>
      )}

      {pdfFile && (
        <PdfPreviewWithPlacement
          pdfFile={pdfFile}
          mode={mode}
          activeSlot={activeSlot}
          onActiveSlotChange={setActiveSlot}
          signatureRect={signatureRect}
          qrRect={qrRect}
          onSignatureRectChange={setSignatureRect}
          onQrRectChange={setQrRect}
          previewContent={{
            text: signerName,
            qrDataUrl,
            drawnImageDataUrl,
            verificationUrlDisplay: `signwiz.app/v/${shortId}`,
          }}
        />
      )}

      {qrSizeWarning && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-3">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-semibold mb-1">El QR es muy chico para escanearse</p>
            <p>{qrSizeWarning}</p>
            <p className="mt-1 text-xs">Andá al tab <strong>QR</strong> y redibujalo más grande, o moveló a un lugar con más espacio.</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSign}
        disabled={!canSign}
        className={`w-full flex items-center justify-center gap-3 py-4 px-8 rounded-xl text-lg font-bold transition-all shadow-lg ${
          canSign
            ? 'bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white hover:shadow-xl'
            : 'bg-gray-200 dark:bg-legal-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSigning ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Firmando...
          </>
        ) : (
          <>
            <FilePen size={22} />
            Firmar Documento
          </>
        )}
      </button>

      {!canSign && !isSigning && (
        <div className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            {!user && 'Necesitás iniciar sesión.'}
            {user && !pdfFile && 'Subí un PDF.'}
            {user && pdfFile && needsSignature && !signatureRect && ' Falta posicionar la firma sobre el PDF.'}
            {user && pdfFile && needsQr && !qrRect && ' Falta posicionar el QR sobre el PDF.'}
            {user && pdfFile && needsSignature && !signerName.trim() && ' Escribí el nombre que va a aparecer firmado.'}
            {user && pdfFile && allRectsPlaced && certificate && !certPassword && ' Ingresá la contraseña del certificado.'}
          </span>
        </div>
      )}
    </div>
  )
}
